import { useEffect, useState, useCallback } from 'react';
import { Search, ScrollText } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Table } from '@/components/ui/Table';
import api from '@/lib/api';
import type { ApiResponse, PaginationMeta } from '@hirenfire/shared';

interface AuditEntry {
  id: string;
  action: string;
  category: string;
  actorId: string;
  actorEmail: string;
  targetId: string | null;
  targetType: string | null;
  metadata: Record<string, unknown>;
  ipAddress: string;
  createdAt: string;
}

export function AdminAuditPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (category) params.set('category', category);
      params.set('page', String(page));
      params.set('limit', '20');

      const res = await api.get<ApiResponse<AuditEntry[]>>(`/admin/audit?${params}`);
      if (res.data.data) setEntries(res.data.data);
      if (res.data.meta) setMeta(res.data.meta);
    } catch {
      // fallback
    } finally {
      setIsLoading(false);
    }
  }, [search, category, page]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const categories = ['AUTH', 'USER', 'JOB', 'CONTRACT', 'PAYMENT', 'KYC', 'ADMIN'];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">Audit Log</h1>
        <p className="text-gray-400 mt-1">Track all platform actions and events</p>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search by actor or action..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>
        <Select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setPage(1);
          }}
          options={[
            { value: '', label: 'All Categories' },
            ...categories.map((c) => ({ value: c, label: c })),
          ]}
        />
      </div>

      {entries.length === 0 && !isLoading ? (
        <Card>
          <CardBody className="py-16 text-center">
            <ScrollText className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No audit entries found</p>
          </CardBody>
        </Card>
      ) : (
        <>
          <Card>
            <Table
              isLoading={isLoading}
              keyExtractor={(e) => e.id}
              columns={[
                {
                  key: 'action',
                  header: 'Action',
                  render: (e) => (
                    <span className="text-white font-medium">{e.action}</span>
                  ),
                },
                {
                  key: 'category',
                  header: 'Category',
                  render: (e) => (
                    <Badge variant="primary">{e.category}</Badge>
                  ),
                },
                {
                  key: 'actor',
                  header: 'Actor',
                  render: (e) => (
                    <span className="text-gray-300 text-sm">{e.actorEmail}</span>
                  ),
                },
                {
                  key: 'target',
                  header: 'Target',
                  render: (e) =>
                    e.targetId ? (
                      <span className="font-mono text-xs text-gray-400">
                        {e.targetType}: {e.targetId.slice(0, 8)}...
                      </span>
                    ) : (
                      <span className="text-gray-600">--</span>
                    ),
                },
                {
                  key: 'ip',
                  header: 'IP',
                  render: (e) => (
                    <span className="font-mono text-xs text-gray-500">{e.ipAddress}</span>
                  ),
                },
                {
                  key: 'date',
                  header: 'Date',
                  render: (e) => (
                    <span className="text-gray-400 text-sm">
                      {new Date(e.createdAt).toLocaleString()}
                    </span>
                  ),
                },
              ]}
              data={entries}
            />
          </Card>

          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-400 px-4">
                Page {meta.page} of {meta.totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= meta.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
